(function () {
  const NAMESPACE = 'NYGLTwinTowersPdp';
  const DEFAULT_VARIANT_ID = '43653226201300';
  const VALID_VARIANT_IDS = new Set([
    '43653226234068',
    '43653226201300',
    '44486202392788',
  ]);
  const TRACKED_SCALE_VARIANT_IDS = new Set([
    '43653226234068',
    '43653226201300',
    '44486202392788',
  ]);
  const ARROW_KEYS = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'];

  if (window[NAMESPACE] && window[NAMESPACE].initAll) {
    window[NAMESPACE].initAll(document);
    return;
  }

  const controllers = new WeakMap();

  function normalizeVariantId(value) {
    if (!value) return DEFAULT_VARIANT_ID;
    const variantId = String(value);
    return VALID_VARIANT_IDS.has(variantId) ? variantId : DEFAULT_VARIANT_ID;
  }

  function urlForVariant(variantId, href) {
    const url = new URL(href || window.location.href);
    url.searchParams.set('variant', variantId);
    return `${url.pathname}${url.search}${url.hash}`;
  }

  function requestedVariantFromHref(href) {
    return new URL(href || window.location.href).searchParams.get('variant');
  }

  function getVariantFromEvent(event) {
    return event.detail && event.detail.variant && event.detail.variant.id
      ? String(event.detail.variant.id)
      : null;
  }

  function getSections(root) {
    const scope = root || document;
    const sections = [];

    if (scope.matches && scope.matches('[data-tt-pdp]')) {
      sections.push(scope);
    }

    scope.querySelectorAll('[data-tt-pdp]').forEach((section) => {
      sections.push(section);
    });

    return sections;
  }

  class TwinTowersPdp {
    constructor(section) {
      this.section = section;
      this.buttons = Array.from(section.querySelectorAll('[data-tt-variant-id]'));
      this.variantSelection = section.querySelector('[data-variant-selection]');
      this.nativeSelect = section.querySelector('[data-tt-native-variant-select]');
      this.liveRegion = section.querySelector('[data-tt-availability-live]');
      this.bisShell = section.querySelector('[data-tt-bis-shell]');
      this.gallery = section.querySelector('[data-tt-pdp-gallery]');
      this.galleryNavigation = section.querySelector('[data-gallery-scroller]');
      this.galleryFigures = Array.from(section.querySelectorAll('[data-tt-gallery-media]'));
      this.galleryThumbnails = Array.from(
        section.querySelectorAll('[data-tt-gallery-thumbnail-variant-id]')
      );
      this.pendingTimers = new Set();
      this.pendingFrames = new Set();
      this.requestFrame = window.requestAnimationFrame
        ? window.requestAnimationFrame.bind(window)
        : function (frameCallback) {
          return window.setTimeout(frameCallback, 16);
        };
      this.cancelFrame = window.cancelAnimationFrame
        ? window.cancelAnimationFrame.bind(window)
        : window.clearTimeout.bind(window);
      this.queueTask = window.queueMicrotask
        ? function (taskCallback) {
          return window.queueMicrotask(taskCallback);
        }
        : function (taskCallback) {
          return Promise.resolve().then(taskCallback);
        };
      this.pendingHistoryAction = null;
      this.pendingHistoryHref = null;
      this.pendingHistoryVariantId = null;
      this.pendingAnalyticsIntent = null;
      this.analyticsIntentSerial = 0;
      this.galleryActivationToken = 0;
      this.galleryCurrentToken = 0;
      this.currentVariantId = null;
      this.fbtObserver = null;
      this.destroyed = false;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleGalleryClick = this.handleGalleryClick.bind(this);
      this.handlePopstate = this.handlePopstate.bind(this);
    }

    init() {
      const initialVariantId = normalizeVariantId(requestedVariantFromHref());

      this.buttons.forEach((button) => {
        button.addEventListener('click', this.handleClick);
        button.addEventListener('keydown', this.handleKeydown);
      });

      if (this.variantSelection) {
        this.variantSelection.addEventListener('variant-change', this.handleVariantChange);
      }

      if (this.galleryNavigation) {
        this.galleryNavigation.addEventListener('click', this.handleGalleryClick);
      }

      window.addEventListener('popstate', this.handlePopstate);

      this.currentVariantId = initialVariantId;
      this.syncOwnership(initialVariantId);
      this.applyNativeVariant(initialVariantId, true);
      this.writeHistory('replace', initialVariantId);
      this.stripSaleMerchandising();
      this.suppressFrequentlyBoughtTogether();
      this.observeFrequentlyBoughtTogether();
      this.scheduleFrame(() => {
        this.stripSaleMerchandising();
        this.suppressFrequentlyBoughtTogether();
      });
      this.queueGalleryCurrentSync();
      this.syncGalleryVariantVisibility(initialVariantId);
    }

    destroy() {
      this.destroyed = true;

      this.buttons.forEach((button) => {
        button.removeEventListener('click', this.handleClick);
        button.removeEventListener('keydown', this.handleKeydown);
      });

      if (this.variantSelection) {
        this.variantSelection.removeEventListener('variant-change', this.handleVariantChange);
      }

      if (this.galleryNavigation) {
        this.galleryNavigation.removeEventListener('click', this.handleGalleryClick);
      }

      window.removeEventListener('popstate', this.handlePopstate);

      this.galleryActivationToken += 1;
      this.galleryCurrentToken += 1;
      this.pendingTimers.forEach((timerId) => window.clearTimeout(timerId));
      this.pendingFrames.forEach((frameId) => this.cancelFrame(frameId));
      this.pendingTimers.clear();
      this.pendingFrames.clear();

      if (this.fbtObserver) {
        this.fbtObserver.disconnect();
        this.fbtObserver = null;
      }

      controllers.delete(this.section);
    }

    selectedButton(variantId) {
      return this.section.querySelector(`[data-tt-variant-id="${variantId}"]`);
    }

    isDeniedVariant(button) {
      if (!button) return false;
      const quantity = Number(button.dataset.ttVariantInventoryQuantity || 0);
      const variantId = button.dataset.ttVariantId;

      if (button.dataset.ttVariantAvailable === 'false') return true;
      return variantId === DEFAULT_VARIANT_ID && quantity <= 0;
    }

    handleClick(event) {
      const button = event.currentTarget;
      const variantId = normalizeVariantId(button.dataset.ttVariantId);
      const trackingSource = event.detail > 0 ? 'click' : null;

      this.selectVariantFromCustomerAction(button, variantId, trackingSource);
    }

    handleKeydown(event) {
      if (!ARROW_KEYS.includes(event.key)) return;

      event.preventDefault();

      const index = this.buttons.indexOf(event.currentTarget);
      const offset = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      const nextIndex = (index + offset + this.buttons.length) % this.buttons.length;
      const nextButton = this.buttons[nextIndex];

      if (nextButton) {
        const variantId = normalizeVariantId(nextButton.dataset.ttVariantId);

        this.selectVariantFromCustomerAction(nextButton, variantId, 'keyboard');
      }
    }

    handleGalleryClick() {
      this.queueGalleryCurrentSync();
    }

    selectVariantFromCustomerAction(button, variantId, source) {
      if (variantId === this.currentVariantId) {
        this.pendingHistoryAction = null;
        this.pendingHistoryHref = null;
        this.pendingHistoryVariantId = null;
        this.clearAnalyticsIntent();
        this.queueGalleryActivation(variantId);
        button.focus();
        this.stripSaleMerchandising();
        return;
      }

      this.pendingHistoryAction = 'push';
      this.pendingHistoryHref = window.location.href;
      this.pendingHistoryVariantId = this.currentVariantId;

      if (source) {
        this.createAnalyticsIntent(variantId, source);
      } else {
        this.clearAnalyticsIntent();
      }

      this.currentVariantId = variantId;
      this.applyNativeVariant(variantId);
      button.focus();
    }

    createAnalyticsIntent(variantId, source) {
      this.analyticsIntentSerial += 1;
      this.pendingAnalyticsIntent = {
        variantId,
        source,
        token: this.analyticsIntentSerial,
      };
    }

    consumeAnalyticsIntent(variantId, historyAction) {
      const intent = this.pendingAnalyticsIntent;

      this.clearAnalyticsIntent();

      if (!intent || historyAction !== 'push' || intent.variantId !== variantId) {
        return null;
      }

      return variantId;
    }

    clearAnalyticsIntent() {
      this.pendingAnalyticsIntent = null;
    }

    handleVariantChange(event) {
      const variantId = normalizeVariantId(
        getVariantFromEvent(event) ||
          (this.variantSelection && this.variantSelection.getAttribute('variant'))
      );
      const historyAction = this.pendingHistoryAction || 'replace';
      const historyHref = this.pendingHistoryHref || window.location.href;
      const analyticsVariantId = this.consumeAnalyticsIntent(variantId, historyAction);

      this.currentVariantId = variantId;
      this.syncOwnership(variantId);

      this.queueTask(() => {
        if (this.destroyed) return;

        this.writeHistory(historyAction, variantId, historyHref);
        this.pendingHistoryAction = null;
        this.pendingHistoryHref = null;
        this.pendingHistoryVariantId = null;
        this.emitScaleSelect(analyticsVariantId);
        this.queueGalleryActivation(variantId);
        this.stripSaleMerchandising();
        this.suppressFrequentlyBoughtTogether();
        this.scheduleFrame(() => this.stripSaleMerchandising());
      });
    }

    handlePopstate() {
      const variantId = normalizeVariantId(requestedVariantFromHref());

      this.clearAnalyticsIntent();
      this.pendingHistoryAction = 'replace';
      this.pendingHistoryHref = window.location.href;
      this.pendingHistoryVariantId = null;
      this.currentVariantId = variantId;
      this.syncOwnership(variantId);
      this.applyNativeVariant(variantId, true);
    }

    applyNativeVariant(variantId, forceChange) {
      if (
        this.variantSelection &&
        this.variantSelection.getAttribute('variant') !== variantId
      ) {
        this.variantSelection.setAttribute('variant', variantId);
      }

      if (this.nativeSelect && (this.nativeSelect.value !== variantId || forceChange)) {
        this.nativeSelect.value = variantId;
        this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncOwnership(variantId) {
      this.buttons.forEach((button) => {
        const selected = button.dataset.ttVariantId === variantId;
        const denied = this.isDeniedVariant(button);

        button.classList.toggle('is-selected', selected);
        button.classList.toggle('is-sold-out', denied);
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.setAttribute('tabindex', selected ? '0' : '-1');
        button.setAttribute('data-tt-bis-state', denied ? 'pending-contract' : 'not-applicable');
      });

      this.syncAvailability(variantId);
      this.syncGalleryVariantVisibility(variantId);
    }

    syncAvailability(variantId) {
      const button = this.selectedButton(variantId);
      const denied = this.isDeniedVariant(button);
      const role = button ? button.dataset.ttVariantRole : '';
      const message = denied
        ? `${role} selected. Sold out.`
        : `${role} selected. Available for purchase.`;

      this.section.setAttribute(
        'data-tt-availability-state',
        denied ? 'sold-out' : 'available'
      );

      if (this.liveRegion) {
        this.liveRegion.textContent = role ? message : '';
      }

      if (this.bisShell) {
        const hasBisIntegration = this.bisShell.dataset.ttBisIntegration === 'active';
        this.bisShell.hidden = !(denied && hasBisIntegration);
        this.bisShell.setAttribute('aria-hidden', denied && hasBisIntegration ? 'false' : 'true');
      }

      this.setAtcState(denied);
    }

    emitScaleSelect(variantId) {
      if (!variantId || !TRACKED_SCALE_VARIANT_IDS.has(variantId)) return;

      const tracker = window.NYGL911LaunchTracking;
      if (!tracker || typeof tracker.emitScaleSelect !== 'function') return;

      tracker.emitScaleSelect(variantId);
    }

    queueGalleryActivation(variantId) {
      if (!this.gallery || !this.galleryThumbnails.length) return;

      this.galleryActivationToken += 1;
      const token = this.galleryActivationToken;

      this.scheduleFrame(() => {
        if (
          this.destroyed ||
          token !== this.galleryActivationToken ||
          variantId !== this.currentVariantId
        ) {
          return;
        }

        this.activateGalleryVariant(variantId);
      });
    }

    activateGalleryVariant(variantId) {
      const targetThumbnail = this.galleryThumbnails.find((thumbnail) => (
        thumbnail.dataset.ttGalleryThumbnailVariantId === variantId &&
        thumbnail.dataset.ttGalleryRank === 'A'
      ));

      if (!targetThumbnail) return;

      const targetIndex = targetThumbnail.dataset.galleryIndex;
      const selectedFigure = this.galleryFigures.find((figure) => (
        figure.dataset.gallerySelected === 'true'
      ));
      const selectedThumbnail = this.galleryThumbnails.find((thumbnail) => (
        thumbnail.dataset.gallerySelected === 'true'
      ));
      const alreadySelected =
        selectedFigure &&
        selectedThumbnail &&
        selectedFigure.dataset.galleryIndex === targetIndex &&
        selectedThumbnail.dataset.galleryIndex === targetIndex;

      this.syncGalleryImagePriority(variantId);

      if (alreadySelected) {
        this.queueGalleryCurrentSync();
        return;
      }

      if (typeof MouseEvent === 'function') {
        targetThumbnail.dispatchEvent(new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
        }));
      } else {
        targetThumbnail.click();
      }

      this.queueGalleryCurrentSync();
    }

    syncGalleryImagePriority(variantId) {
      this.galleryFigures.forEach((figure) => {
        const selected =
          figure.dataset.ttGalleryVariantId === variantId &&
          figure.dataset.ttGalleryRank === 'A';
        const image = figure.querySelector('[data-tt-gallery-image]');

        if (image) {
          image.setAttribute('loading', selected ? 'eager' : 'lazy');
          image.setAttribute('fetchpriority', selected ? 'high' : 'auto');
        }
      });
    }

    syncGalleryVariantVisibility(variantId) {
      this.galleryThumbnails.forEach((thumbnail) => {
        const visible = thumbnail.dataset.ttGalleryThumbnailVariantId === variantId;

        thumbnail.hidden = !visible;
        thumbnail.setAttribute('aria-hidden', visible ? 'false' : 'true');
        thumbnail.setAttribute('tabindex', visible ? '0' : '-1');

        if (!visible) {
          thumbnail.removeAttribute('aria-current');
        }
      });
    }

    queueGalleryCurrentSync() {
      if (!this.galleryThumbnails.length) return;

      this.galleryCurrentToken += 1;
      const token = this.galleryCurrentToken;

      this.scheduleFrame(() => {
        if (this.destroyed || token !== this.galleryCurrentToken) return;

        this.syncGalleryCurrent();
      });
    }

    syncGalleryCurrent() {
      const selectedThumbnail = this.galleryThumbnails.find((thumbnail) => (
        thumbnail.dataset.gallerySelected === 'true'
      ));

      this.galleryThumbnails.forEach((thumbnail) => {
        if (thumbnail === selectedThumbnail) {
          thumbnail.setAttribute('aria-current', 'true');
        } else {
          thumbnail.removeAttribute('aria-current');
        }
      });
    }

    setAtcState(denied) {
      const button = this.section.querySelector('[data-product-atc]');
      const text = button ? button.querySelector('.atc-button--text') : null;

      if (!button) return;

      button.disabled = denied;
      button.classList.toggle('disabled', denied);
      button.setAttribute('aria-disabled', denied ? 'true' : 'false');

      if (text) {
        text.textContent = denied ? 'Sold out' : 'Add to cart';
      }
    }

    writeHistory(action, variantId, href) {
      const nextUrl = urlForVariant(variantId, href);
      const state = { ttVariantId: variantId };

      if (action === 'push') {
        if (this.pendingHistoryVariantId) {
          window.history.replaceState(
            { ttVariantId: this.pendingHistoryVariantId },
            '',
            urlForVariant(this.pendingHistoryVariantId, href)
          );
        }

        window.history.pushState(state, '', nextUrl);
      } else {
        window.history.replaceState(state, '', nextUrl);
      }
    }

    stripSaleMerchandising() {
      const saleBadge = this.section.querySelector('[data-badge-sales]');
      const compareContainer = this.section.querySelector('[data-price-compare-container]');
      const currentPrice = this.section.querySelector('[data-price-container]');

      if (saleBadge) {
        saleBadge.remove();
      }

      if (compareContainer) {
        compareContainer.classList.remove('visible');
        compareContainer.setAttribute('aria-hidden', 'true');
        compareContainer.innerHTML = '';
      }

      if (currentPrice) {
        currentPrice.classList.remove('price__current--on-sale');
      }
    }

    suppressFrequentlyBoughtTogether() {
      this.section.querySelectorAll('.cbb-frequently-bought-container').forEach((container) => {
        container.remove();
      });
    }

    observeFrequentlyBoughtTogether() {
      if (!window.MutationObserver || this.fbtObserver) return;

      this.fbtObserver = new MutationObserver(() => {
        if (this.destroyed) return;

        this.suppressFrequentlyBoughtTogether();
      });

      this.fbtObserver.observe(this.section, {
        childList: true,
        subtree: true,
      });
    }

    scheduleFrame(callback) {
      const frameId = this.requestFrame(() => {
        this.pendingFrames.delete(frameId);
        if (!this.destroyed) callback();
      });

      this.pendingFrames.add(frameId);
    }
  }

  function initSection(section) {
    const existing = controllers.get(section);

    if (existing) {
      existing.destroy();
    }

    const controller = new TwinTowersPdp(section);
    controllers.set(section, controller);
    controller.init();
  }

  function destroySection(section) {
    const controller = controllers.get(section);

    if (controller) {
      controller.destroy();
    }
  }

  function initAll(root) {
    getSections(root).forEach(initSection);
  }

  function destroyAll(root) {
    getSections(root).forEach(destroySection);
  }

  window[NAMESPACE] = {
    initAll,
    destroyAll,
  };

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }
})();
